import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { formatApiErrorDetail, apiError } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { t } = useI18n();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) await login(email.trim(), password);
      else await register(email.trim(), password, name.trim());
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err, t));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative mx-auto flex min-h-screen max-w-md flex-col bg-[#F8FAF8]">
      <div className="flex justify-end p-4">
        <LanguageSwitcher />
      </div>

      <div className="flex flex-1 flex-col justify-center px-6 pb-16">
        <div className="esv-rise mb-8 flex flex-col items-center text-center">
          <img src="/icons/icon-192.png" alt="ESVAN" className="mb-4 h-16 w-16 rounded-2xl shadow-lg" />
          <h1 className="font-display text-3xl font-extrabold text-[#1A2E26]">{t("auth.welcome")}</h1>
          <p className="mt-2 max-w-xs text-sm text-[#7A9488]">{t("auth.subtitle")}</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-[#B0C2B8]">{t("common.tagline")}</p>
        </div>

        <div className="esv-rise rounded-3xl border border-[#E2EAE5] bg-white p-6 shadow-sm" style={{ animationDelay: "80ms" }}>
          {/* Tab toggle */}
          <div className="mb-5 flex rounded-full bg-[#F0F4F1] p-1">
            <button
              data-testid="auth-tab-login"
              onClick={() => setMode("login")}
              className={`flex-1 rounded-full py-2 text-sm font-semibold transition-all ${isLogin ? "bg-white text-[#2D5A4C] shadow-sm" : "text-[#7A9488]"}`}
            >
              {t("auth.login")}
            </button>
            <button
              data-testid="auth-tab-signup"
              onClick={() => setMode("signup")}
              className={`flex-1 rounded-full py-2 text-sm font-semibold transition-all ${!isLogin ? "bg-white text-[#2D5A4C] shadow-sm" : "text-[#7A9488]"}`}
            >
              {t("auth.signup")}
            </button>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {!isLogin && (
              <div className="relative">
                <UserIcon size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7A9488]" />
                <Input
                  data-testid="auth-name-input"
                  className="h-12 rounded-xl pl-10"
                  placeholder={t("auth.name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            )}
            <div className="relative">
              <Mail size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7A9488]" />
              <Input
                data-testid="auth-email-input"
                type="email"
                className="h-12 rounded-xl pl-10"
                placeholder={t("auth.email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="relative">
              <Lock size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7A9488]" />
              <Input
                data-testid="auth-password-input"
                type="password"
                className="h-12 rounded-xl pl-10"
                placeholder={t("auth.password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {isLogin && (
              <div className="text-right">
                <button type="button" data-testid="auth-forgot-link" onClick={() => navigate("/forgot-password")}
                  className="text-xs font-semibold text-[#2D5A4C] hover:underline">{t("reset.forgot")}</button>
              </div>
            )}

            <Button
              data-testid={isLogin ? "auth-login-button" : "auth-signup-button"}
              type="submit"
              disabled={loading}
              className="h-12 w-full rounded-xl bg-[#2D5A4C] text-base font-semibold hover:bg-[#214439]"
            >
              {loading ? (isLogin ? t("auth.loggingIn") : t("auth.creating")) : isLogin ? t("auth.loginCta") : t("auth.signupCta")}
            </Button>
          </form>

          <div className="my-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-[#E2EAE5]" />
            <span className="text-xs text-[#B0C2B8]">{t("auth.or")}</span>
            <div className="h-px flex-1 bg-[#E2EAE5]" />
          </div>

          <button
            data-testid="auth-google-button"
            onClick={googleLogin}
            className="flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-[#E2EAE5] bg-white text-sm font-semibold text-[#1A2E26] transition-colors hover:bg-[#F0F4F1] active:scale-[0.99]"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            {t("auth.google")}
          </button>
        </div>
      </div>
    </div>
  );
}
